/* eslint-disable no-unused-vars */
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Search,
  MapPin,
  Check,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Loader2,
  Phone,
  Briefcase,
  Clock,
  Navigation,
  Calendar,
  MessageSquare,
  Home,
} from "lucide-react";
import Step2ServiceDetails from "../caregiver/addcaregiver/Step2ServiceDetails";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import {
  CHILDCARE_AGES,
  ELDERLY_CARE_NEEDS,
  EXPERIENCE_LEVELS,
  FAMILY_SIZES,
  LANGUAGES,
  SUBCITIES,
  TUTOR_LEVELS,
  WEEK_DAYS,
  WORK_HOUR_OPTIONS,
} from "../../../utils/constants";

const WORK_TIMES = ["part-time", "on-demand", "living-in", "night-shift"];

const PostServiceRequest = () => {
  const {
    clients,
    clientsPagination,
    getAllClients,
    postJobForClient,
    serviceCategories,
    fetchCategoryFields,
    aToken,
    backendUrl,
  } = useContext(AdminContext);

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [clientType, setClientType] = useState("existing");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  // Search / Pagination State for Step 1
  const [clientSearch, setClientSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Form State
  const [formData, setFormData] = useState({
    // Client Selection
    existingClient: "",
    newClientEmail: "",
    newClientName: "",
    newClientPhone: "",

    // New Client Address
    address: {
      line1: "",
      line2: "",
    },
    newClientSubcity: "",

    // Category & Basic Info
    category: "",
    workTime: "part-time",
    description: "",
    experienceLevel: "",
    location: "",
    subcity: "",
    languages: [],

    // Childcare
    age_groups: [],

    // Cook
    service_frequency: 0,

    preferred_work_hours: [],
    family_size: "",

    // Cleaner
    room_numbers: 0,

    // Home Tutor
    teaching_levels: [],
    hours_per_day: 0,

    // Elderly Care
    elderly_care_experience: "No",
    first_aid_cpr_certified: "No",
    transportation_assistance: "No",
    meal_preparation: "No",
    light_housekeeping: "No",
    medication_management: "No",
    mobility_assistance: "No",
    companionship: "No",

    preferred_days: [],
    additionalPreferences: "",
  });

  const [categoryFields, setCategoryFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  const [errors, setErrors] = useState({});
  const [postingError, setPostingError] = useState(null);

  // Fetch Clients
  const fetchClients = useCallback(
    async (page = 1, search = "", limit = itemsPerPage) => {
      try {
        setIsLoadingClients(true);
        await getAllClients({
          page,
          limit,
          search: search.trim(),
          status: "All",
          gender: "all",
        });
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setIsLoadingClients(false);
        setIsSearching(false);
      }
    },
    [getAllClients, itemsPerPage],
  );

  // Initial load
  useEffect(() => {
    fetchClients(currentPage, clientSearch);
  }, []);

  // Trigger search function (for search on icon click or enter)
  const triggerSearch = () => {
    setCurrentPage(1);
    fetchClients(1, clientSearch);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchClients(newPage, clientSearch);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchClients(1, clientSearch, newItemsPerPage);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setClientSearch(value);
  };

  // Handle enter key in search
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      triggerSearch(); // Search on Enter
    }
  };

  // Search icon click handler
  const handleSearchIconClick = () => {
    triggerSearch();
  };

  // --- Handlers ---

  const handleSelectClient = (client) => {
    setFormData((prev) => ({
      ...prev,
      existingClient: client.email || "",
    }));
    setErrors({});
  };

  const handleInputChange = (e, fieldName = null) => {
    const { name, value, type } = e.target;
    const field = fieldName || name;

    if (type === "number") {
      setFormData((prev) => ({
        ...prev,
        [field]: value === "" ? "" : Number(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (errors[field]) {
      setErrors((prev) => {
        const newErrs = { ...prev };
        delete newErrs[field];
        return newErrs;
      });
    }
  };

  // Handle address field changes (simplified)
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));

    if (errors[`address.${name}`]) {
      setErrors((prev) => {
        const newErrs = { ...prev };
        delete newErrs[`address.${name}`];
        return newErrs;
      });
    }
  };

  const handleCategorySelect = (catId) => {
    setFormData((prev) => ({
      ...prev,
      category: catId,
    }));
    setErrors({});
  };

  useEffect(() => {
    if (!formData.category) return;
    const fetchFields = async () => {
      try {
        // Use AdminContext method to fetch category fields
        const fields = await fetchCategoryFields(formData.category);
        setCategoryFields(fields || []);
        setFieldValues({}); // Reset field values on category change
      } catch (error) {
        console.error("Failed to fetch category fields", error);
      }
    };
    fetchFields();
  }, [formData.category, fetchCategoryFields]);

  // Resolve the selected category's system name for comparisons
  const selectedCatName = (
    serviceCategories.find((c) => c.id === formData.category)?.name || ""
  ).toLowerCase();
  // Helper: check if selected category name includes a keyword
  const isCat = (...keywords) =>
    keywords.some((k) => selectedCatName.includes(k.toLowerCase()));

  // Toggle helpers for arrays
  const toggleArrayItem = (field, value) => {
    setFormData((prev) => {
      const list = prev[field];
      const newList = list.includes(value)
        ? list.filter((i) => i !== value)
        : [...list, value];
      return { ...prev, [field]: newList };
    });
  };

  const toggleWorkHour = (value) => {
    setFormData((prev) => {
      const current = prev.preferred_work_hours || [];
      const newList = current.includes(value)
        ? current.filter((h) => h !== value)
        : [...current, value];
      return { ...prev, preferred_work_hours: newList };
    });
  };

  const togglePreferredDay = (day) => {
    setFormData((prev) => {
      const current = prev.preferred_days || [];
      const newList = current.includes(day)
        ? current.filter((d) => d !== day)
        : [...current, day];
      return { ...prev, preferred_days: newList };
    });
  };

  const toggleElderlySkill = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field] === "Yes" ? "No" : "Yes",
    }));
  };

  // --- Validation ---
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (clientType === "existing" && !formData.existingClient) {
        newErrors.existingClient = "Please select a client";
      } else if (clientType === "new") {
        if (!formData.newClientEmail)
          newErrors.newClientEmail = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.newClientEmail))
          newErrors.newClientEmail = "Email is invalid";

        if (!formData.newClientName)
          newErrors.newClientName = "Name is required";

        if (!formData.newClientPhone)
          newErrors.newClientPhone = "Phone number is required";
        else if (!/^[0-9+\-\s()]{10,}$/.test(formData.newClientPhone))
          newErrors.newClientPhone = "Please enter a valid phone number";

        // Address validation for new clients
        if (!formData.address.line1) {
          newErrors["address.line1"] = "Address line 1 is required";
        }

        // Subcity validation for new clients
        if (!formData.newClientSubcity) {
          newErrors.newClientSubcity = "Subcity is required";
        }
      }
    }

    if (step === 2) {
      if (!formData.category) newErrors.category = "Please select a category";
    }

    if (step === 3) {
      // Common required fields for all categories
      if (!formData.description)
        newErrors.description = "Description is required";
      if (!formData.experienceLevel)
        newErrors.experienceLevel = "Experience level is required";
      if (!formData.location) newErrors.location = "Location is required";
      if (!formData.subcity) newErrors.subcity = "Subcity is required";
      if (!formData.workTime) newErrors.workTime = "Work time is required";
      if (!formData.languages || formData.languages.length === 0)
        newErrors.languages = "At least one language is required";

      if (
        !formData.preferred_work_hours ||
        formData.preferred_work_hours.length === 0
      ) {
        newErrors.preferred_work_hours =
          "At least one preferred work hour is required";
      }
      if (!formData.preferred_days || formData.preferred_days.length === 0) {
        newErrors.preferred_days = "At least one preferred day is required";
      }

      // Validate required dynamic category fields
      categoryFields.forEach((field) => {
        if (field.isRequired) {
          const values = fieldValues[field.id] || [];
          if (values.length === 0 || (values.length === 1 && !values[0])) {
            newErrors[field.id] = `${field.label} is required`;
          }
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Scroll to top to show validation errors
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Log validation errors for debugging
      console.log("Validation failed for step", currentStep, "Errors:", errors);
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
    setPostingError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (validateStep(currentStep)) {
      setIsSubmitting(true);
      setPostingError(null);

      const formattedFieldValues = Object.entries(fieldValues)
        .filter(([_, values]) => values && values.length > 0)
        .map(([fieldId, values]) => ({ fieldId, values }));

      // Prepare data in the format expected by the API
      const { category, ...restFormData } = formData;
      const jobData = {
        ...restFormData,
        serviceCategoryId: category,
        location: formData.location?.trim(),
        fieldValues: formattedFieldValues,
        // For existing clients - send clientId instead of email
        ...(clientType === "existing" && {
          clientId:
            clients.find((c) => c.email === formData.existingClient)?.id ||
            null,
        }),
        // For new clients - explicitly include simplified address
        ...(clientType === "new" && {
          email: formData.newClientEmail,
          name: formData.newClientName,
          phoneNumber: formData.newClientPhone,
          subcity: formData.newClientSubcity,
          address: {
            line1: formData.address.line1,
            line2: formData.address.line2,
          },
        }),
      };

      const result = await postJobForClient(jobData);

      if (result.success) {
        setSuccessMsg("Service Request Posted Successfully!");
        setTimeout(() => {
          setSuccessMsg("");
          navigate("/requests/manage");
        }, 2000);
      } else {
        setPostingError({
          type: result.type,
          message: result.message,
        });
      }

      setIsSubmitting(false);
    }
  };

  // --- Render Steps ---

  const renderClientSelection = () => (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-text dark:text-dark-text">
          Client Selection
        </h3>
      </div>

      <div className="mb-6">
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => {
              setClientType("existing");
              setPostingError(null);
            }}
            className={`px-4 py-2 rounded-2xl text-sm font-bold transition-colors ${
              clientType === "existing"
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-dark-text"
            }`}
          >
            Existing Client
          </button>
          <button
            onClick={() => {
              setClientType("new");
              setPostingError(null);
            }}
            className={`px-4 py-2 rounded-2xl text-sm font-bold transition-colors ${
              clientType === "new"
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-dark-text"
            }`}
          >
            New Client
          </button>
        </div>

        {clientType === "existing" ? (
          <div className="space-y-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search client by name, email, phone or address..."
                value={clientSearch}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                className="w-full pl-4 pr-12 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-dark-border focus:outline-none focus:ring-2 focus:ring-primary dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-muted"
              />
              <button
                onClick={handleSearchIconClick}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-primary hover:text-primary/80 transition-colors"
                type="button"
              >
                <Search size={18} />
              </button>
            </div>

            <div className="border border-gray-100 dark:border-dark-border rounded-3xl overflow-hidden min-h-[200px]">
              {isLoadingClients ? (
                <div className="p-8 text-center text-gray-500 dark:text-dark-muted flex flex-col items-center justify-center">
                  <Loader2 className="animate-spin mb-2" size={24} />
                  Loading clients...
                </div>
              ) : clients && clients.length > 0 ? (
                clients.map((client) => (
                  <div
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                    className={`p-4 flex items-center justify-between cursor-pointer border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                      formData.existingClient === client.email
                        ? "bg-primary/20"
                        : "bg-card dark:bg-dark-surface"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                        <User size={20} />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-text dark:text-dark-text">
                          {client.name || "Unnamed Client"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-dark-muted">
                          {client.email || "No email"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-dark-muted">
                          {client.phoneNumber || "No phone"}
                        </p>
                        {client.address &&
                          (client.address.line1 || client.address.line2) && (
                            <p className="text-xs text-gray-500 dark:text-dark-muted truncate max-w-xs flex items-center gap-1">
                              <MapPin size={10} />
                              {client.address.line1 || ""}
                              {client.address.line2
                                ? `, ${client.address.line2}`
                                : ""}
                            </p>
                          )}
                      </div>
                    </div>
                    {formData.existingClient === client.email && (
                      <div className="text-primary">
                        <Check size={18} />
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500 dark:text-dark-muted">
                  {clientSearch
                    ? "No clients match your search."
                    : "No clients found in the system."}
                </div>
              )}
            </div>

            {clientsPagination && clientsPagination.totalPages > 1 && (
              <div className="pt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={clientsPagination.totalPages}
                  onPageChange={handlePageChange}
                  itemsPerPage={itemsPerPage}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  totalItems={clientsPagination.total}
                />
              </div>
            )}

            {errors.existingClient && (
              <p className="text-red-500 text-sm flex items-center gap-2 mt-2">
                <AlertCircle size={16} /> {errors.existingClient}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-dark-border space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                <User size={18} />
              </div>
              <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase">
                New Client Details
              </h4>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="newClientName"
                  value={formData.newClientName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 dark:placeholder-dark-muted"
                  placeholder="Enter full name"
                />
                {errors.newClientName && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.newClientName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="newClientEmail"
                  value={formData.newClientEmail}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 dark:placeholder-dark-muted"
                  placeholder="client@example.com"
                />
                {errors.newClientEmail && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.newClientEmail}
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="tel"
                    name="newClientPhone"
                    value={formData.newClientPhone}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 dark:placeholder-dark-muted"
                    placeholder="+251 9XX XXX XXX"
                  />
                </div>
                {errors.newClientPhone && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.newClientPhone}
                  </p>
                )}
              </div>
            </div>

            {/* Simplified Address Section - Only Line 1 and Line 2 */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={18} className="text-primary" />
                <h5 className="text-sm font-bold text-gray-900 dark:text-white">
                  Client Address *
                </h5>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                    Subcity *
                  </label>
                  <select
                    name="newClientSubcity"
                    value={formData.newClientSubcity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select Subcity</option>
                    {SUBCITIES.map((subcity) => (
                      <option key={subcity} value={subcity}>
                        {subcity}
                      </option>
                    ))}
                  </select>
                  {errors.newClientSubcity && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.newClientSubcity}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                    Address Line 1 *
                  </label>
                  <div className="relative">
                    <Home
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <input
                      type="text"
                      name="line1"
                      value={formData.address.line1}
                      onChange={handleAddressChange}
                      className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Street address, house number"
                    />
                  </div>
                  {errors["address.line1"] && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors["address.line1"]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                    Address Line 2 (Optional)
                  </label>
                  <input
                    type="text"
                    name="line2"
                    value={formData.address.line2}
                    onChange={handleAddressChange}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Apartment, suite, building, floor, etc."
                  />
                </div>
              </div>
            </div>

            <div className="bg-blue-100 dark:bg-blue-800 p-4 rounded-2xl border border-blue-100 dark:border-blue-800">
              <p className="text-xs text-blue-800 dark:text-blue-200 flex items-center gap-2">
                <AlertCircle size={14} />A new account will be created with the
                phone number as the password.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderCategorySelection = () => (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h3 className="text-xl font-bold text-text dark:text-dark-text mb-6">
        Select Category
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {serviceCategories.map((cat) => {
          const isSelected = formData.category === cat.id;
          return (
            <div
              key={cat.id}
              onClick={() => handleCategorySelect(cat.id)}
              className={`cursor-pointer rounded-3xl p-6 flex flex-col items-center justify-center gap-4 border-2 transition-all duration-200 ${
                isSelected
                  ? "border-primary bg-primary/20 shadow-lg"
                  : "border-transparent bg-gray-100 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-transform overflow-hidden ${
                  isSelected ? "scale-110" : ""
                }`}
                style={{ backgroundColor: cat.color + "20", color: cat.color }}
              >
                {cat.iconImage ? (
                  <img
                    src={cat.iconImage}
                    alt={cat.label}
                    className="w-full h-full object-cover p-2"
                  />
                ) : (
                  <span className="text-2xl">📋</span>
                )}
              </div>
              <span
                className={`font-bold text-sm text-center ${
                  isSelected
                    ? "text-text dark:text-dark-text"
                    : "text-gray-600 dark:text-dark-muted"
                }`}
              >
                {cat.label}
              </span>
            </div>
          );
        })}
      </div>
      {errors.category && (
        <p className="text-red-500 text-sm flex items-center gap-2 mt-4">
          <AlertCircle size={16} /> {errors.category}
        </p>
      )}
    </div>
  );

  const renderJobDetails = () => (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h3 className="text-xl font-bold text-text dark:text-dark-text mb-6">
        Job Details:{" "}
        <span className="text-primary">
          {serviceCategories.find((c) => c.id === formData.category)?.label}
        </span>
      </h3>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
            Description *
          </label>
          <textarea
            rows={4}
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary resize-none placeholder-gray-400 dark:placeholder-dark-muted"
            placeholder="Describe the job requirements..."
          />
          {errors.description && (
            <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
              <AlertCircle size={12} /> {errors.description}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
              Experience Level *
            </label>
            <select
              name="experienceLevel"
              value={formData.experienceLevel}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select Experience Level</option>
              {EXPERIENCE_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level === "0"
                    ? "No experience"
                    : level === "below_1"
                      ? "Below 1 year"
                      : `${level} years`}
                </option>
              ))}
            </select>
            {errors.experienceLevel && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle size={12} /> {errors.experienceLevel}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
              Location *
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 dark:placeholder-dark-muted"
              placeholder="e.g. Bole, Addis Ababa"
            />
            {errors.location && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle size={12} /> {errors.location}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
              Work Time *
            </label>
            <select
              name="workTime"
              value={formData.workTime}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {WORK_TIMES.map((time) => (
                <option key={time} value={time}>
                  {time
                    .replace("-", " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
            {errors.workTime && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle size={12} /> {errors.workTime}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
              Subcity *
            </label>
            <select
              name="subcity"
              value={formData.subcity}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select Subcity</option>
              {SUBCITIES.map((subcity) => (
                <option key={subcity} value={subcity}>
                  {subcity}
                </option>
              ))}
            </select>
            {errors.subcity && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle size={12} /> {errors.subcity}
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
            Languages *
          </label>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((language) => (
              <button
                key={language}
                type="button"
                onClick={() => toggleArrayItem("languages", language)}
                className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                  formData.languages.includes(language)
                    ? "bg-primary text-white border-primary"
                    : "bg-white dark:bg-gray-800 border-gray-300 dark:border-dark-border text-gray-600 dark:text-dark-muted"
                }`}
              >
                {language}
              </button>
            ))}
          </div>
          {errors.languages && (
            <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
              <AlertCircle size={12} /> {errors.languages}
            </p>
          )}
        </div>

        <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-dark-border">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={18} className="text-primary" />
            <label className="text-sm font-semibold text-gray-700 dark:text-dark-text">
              Preferred Work Hours *
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {WORK_HOUR_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleWorkHour(option.value)}
                className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  formData.preferred_work_hours?.includes(option.value)
                    ? "bg-primary text-white border-primary shadow-md"
                    : "bg-white dark:bg-gray-800 border-gray-200 dark:border-dark-border text-gray-600 dark:text-dark-muted hover:border-primary"
                }`}
              >
                {formData.preferred_work_hours?.includes(option.value) && (
                  <Check size={14} />
                )}
                {option.label}
              </button>
            ))}
          </div>
          {errors.preferred_work_hours && (
            <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
              <AlertCircle size={12} /> {errors.preferred_work_hours}
            </p>
          )}
        </div>

        <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-dark-border">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={18} className="text-primary" />
            <label className="text-sm font-semibold text-gray-700 dark:text-dark-text">
              Preferred Days *
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {WEEK_DAYS.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => togglePreferredDay(day)}
                className={`px-3 py-3 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  formData.preferred_days?.includes(day)
                    ? "bg-primary text-white border-primary shadow-md"
                    : "bg-white dark:bg-gray-800 border-gray-200 dark:border-dark-border text-gray-600 dark:text-dark-muted hover:border-primary"
                }`}
              >
                {formData.preferred_days?.includes(day) && <Check size={14} />}
                {day.slice(0, 3)}
              </button>
            ))}
          </div>
          {errors.preferred_days && (
            <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
              <AlertCircle size={12} /> {errors.preferred_days}
            </p>
          )}
        </div>

        {/* Category-specific dynamic fields */}
        <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-dark-border">
          <Step2ServiceDetails
            categoryFields={categoryFields}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            categoryLabel={
              serviceCategories.find((c) => c.id === formData.category)
                ?.label || "Service"
            }
            errors={errors}
          />
        </div>

        <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-dark-border">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare size={18} className="text-primary" />
            <label className="text-sm font-semibold text-gray-700 dark:text-dark-text">
              Additional Preferences (Optional)
            </label>
          </div>
          <textarea
            rows={3}
            name="additionalPreferences"
            value={formData.additionalPreferences}
            onChange={handleInputChange}
            className="w-full px-4 py-3 rounded-2xl border border-gray-100 dark:border-dark-border dark:bg-gray-800 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary resize-none placeholder-gray-400 dark:placeholder-dark-muted"
            placeholder="Any additional requirements or preferences..."
          />
        </div>
      </div>
    </div>
  );

  const renderReview = () => (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h3 className="text-xl font-bold text-text dark:text-dark-text mb-6">
        Review & Submit
      </h3>

      <div className="space-y-6">
        {/* Client Information Card */}
        <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-dark-border">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-bold text-gray-500 dark:text-dark-muted uppercase text-xs">
              Client Information
            </h4>
            <button
              onClick={() => setCurrentStep(1)}
              className="text-primary text-xs font-bold hover:underline"
            >
              Edit
            </button>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
              <User size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-lg text-text dark:text-dark-text">
                {clientType === "existing"
                  ? formData.existingClient
                  : formData.newClientName}
              </p>
              <p className="text-gray-500 dark:text-dark-muted text-sm">
                {clientType === "existing"
                  ? formData.existingClient
                  : formData.newClientEmail}
              </p>
              {clientType === "new" && (
                <div className="mt-3 space-y-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <Phone size={14} />
                    {formData.newClientPhone}
                  </p>
                  {formData.newClientSubcity && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                      <MapPin size={14} />
                      {formData.newClientSubcity}
                    </p>
                  )}
                  {(formData.address.line1 || formData.address.line2) && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                      <Home size={14} className="mt-1 flex-shrink-0" />
                      <span className="truncate">
                        {formData.address.line1}
                        {formData.address.line2 &&
                          `, ${formData.address.line2}`}
                      </span>
                    </div>
                  )}
                  <span className="inline-block mt-2 px-2 py-1 bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300 text-xs rounded-md font-medium">
                    New Client Account
                  </span>
                </div>
              )}
              {clientType === "existing" && (
                <span className="inline-block mt-2 px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-md font-medium">
                  Existing Client
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Job Details Card */}
        <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-dark-border">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-bold text-gray-500 dark:text-dark-muted uppercase text-xs">
              Job Details
            </h4>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentStep(2)}
                className="text-primary text-xs font-bold hover:underline"
              >
                Edit Category
              </button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <button
                onClick={() => setCurrentStep(3)}
                className="text-primary text-xs font-bold hover:underline"
              >
                Edit Details
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <span
                className="px-3 py-1.5 rounded-full text-xs font-bold"
                style={{
                  backgroundColor:
                    serviceCategories.find((c) => c.id === formData.category)
                      ?.color + "20",
                  color:
                    serviceCategories.find((c) => c.id === formData.category)
                      ?.color || "#6B7280",
                }}
              >
                {
                  serviceCategories.find((c) => c.id === formData.category)
                    ?.label
                }
              </span>
              <span className="text-sm text-gray-500 dark:text-dark-muted capitalize bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-md">
                {formData.workTime.replace(/-/g, " ")}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-dark-border">
                <span className="block text-gray-400 dark:text-dark-muted text-xs mb-1">
                  Location
                </span>
                <p className="font-semibold text-text dark:text-dark-text">
                  {formData.location}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-dark-border">
                <span className="block text-gray-400 dark:text-dark-muted text-xs mb-1">
                  Subcity
                </span>
                <p className="font-semibold text-text dark:text-dark-text">
                  {formData.subcity || "Not specified"}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-dark-border">
                <span className="block text-gray-400 dark:text-dark-muted text-xs mb-1">
                  Experience
                </span>
                <p className="font-semibold text-text dark:text-dark-text">
                  {formData.experienceLevel} years
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-dark-border">
                <span className="block text-gray-400 dark:text-dark-muted text-xs mb-1">
                  Languages
                </span>
                <p className="font-semibold text-text dark:text-dark-text truncate">
                  {formData.languages.length > 0
                    ? formData.languages.join(", ")
                    : "Any"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-4">
              <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-dark-border">
                <span className="block text-gray-400 dark:text-dark-muted text-xs mb-1">
                  Preferred Hours
                </span>
                <p className="font-semibold text-text dark:text-dark-text">
                  {formData.preferred_work_hours?.length > 0
                    ? formData.preferred_work_hours.join(", ")
                    : "Not specified"}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-dark-border">
                <span className="block text-gray-400 dark:text-dark-muted text-xs mb-1">
                  Preferred Days
                </span>
                <p className="font-semibold text-text dark:text-dark-text">
                  {formData.preferred_days?.length > 0
                    ? formData.preferred_days.join(", ")
                    : "Not specified"}
                </p>
              </div>
            </div>

            {formData.description && (
              <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-dark-border">
                <span className="block text-gray-400 dark:text-dark-muted text-xs mb-1 uppercase tracking-wider">
                  Description
                </span>
                <p className="text-sm text-gray-700 dark:text-dark-text leading-relaxed">
                  {formData.description}
                </p>
              </div>
            )}

            {formData.additionalPreferences && (
              <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-dark-border">
                <span className="block text-gray-400 dark:text-dark-muted text-xs mb-1 uppercase tracking-wider">
                  Additional Preferences
                </span>
                <p className="text-sm text-gray-700 dark:text-dark-text leading-relaxed">
                  {formData.additionalPreferences}
                </p>
              </div>
            )}

            {/* Category Specific Summary */}
            <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-dark-border mt-6">
              <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase mb-4">
                Category Specific Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryFields.map((field) => {
                  const vals = fieldValues[field.id] || [];
                  if (vals.length === 0) return null;
                  return (
                    <div
                      key={field.id}
                      className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-dark-border"
                    >
                      <span className="text-gray-400 dark:text-dark-muted text-xs block mb-1">
                        {field.label}
                      </span>
                      <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                        {vals.join(", ")}
                      </span>
                    </div>
                  );
                })}
                {Object.values(fieldValues).flat().length === 0 && (
                  <span className="text-sm text-gray-500 col-span-full">
                    No specific details provided.
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {postingError && (
          <div
            className={`rounded-2xl border p-5 ${
              postingError.type === "appointment-conflict"
                ? "bg-orange-100 dark:bg-orange-800 border-orange-200 dark:border-orange-800"
                : postingError.type === "job-limit"
                  ? "bg-red-100 dark:bg-red-800 border-red-200 dark:border-red-800"
                  : postingError.type === "duplicate-email" ||
                      postingError.type === "duplicate-phone"
                    ? "bg-yellow-100 dark:bg-yellow-800 border-yellow-200 dark:border-yellow-800"
                    : "bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`p-2 rounded-full flex-shrink-0 ${
                  postingError.type === "appointment-conflict"
                    ? "bg-orange-100 dark:bg-orange-800 text-orange-600 dark:text-orange-300"
                    : postingError.type === "job-limit"
                      ? "bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-300"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                }`}
              >
                <AlertCircle size={20} />
              </div>
              <div className="flex-1">
                <h4
                  className={`font-bold text-base mb-1 ${
                    postingError.type === "appointment-conflict"
                      ? "text-orange-900 dark:text-orange-200"
                      : postingError.type === "job-limit"
                        ? "text-red-900 dark:text-red-200"
                        : "text-gray-900 dark:text-gray-100"
                  }`}
                >
                  {postingError.type === "appointment-conflict" &&
                    "Active Appointment Exists"}
                  {postingError.type === "job-limit" &&
                    "Active Job in Category"}
                  {postingError.type === "duplicate-email" &&
                    "Email Already Registered"}
                  {postingError.type === "duplicate-phone" &&
                    "Phone Number Exists"}
                  {!postingError.type && "Error"}
                </h4>
                <p
                  className={`text-sm mb-4 ${
                    postingError.type === "appointment-conflict"
                      ? "text-orange-700 dark:text-orange-300"
                      : postingError.type === "job-limit"
                        ? "text-red-700 dark:text-red-300"
                        : "text-gray-600 dark:text-gray-400"
                  }`}
                >
                  {postingError.message}
                </p>

                <div className="flex flex-wrap gap-2">
                  {postingError.type === "appointment-conflict" && (
                    <>
                      <button
                        onClick={() => navigate("/bookings")}
                        className="px-4 py-2 bg-white dark:bg-gray-800 border border-orange-300 dark:border-orange-600 text-orange-700 dark:text-orange-300 rounded-xl text-sm font-semibold hover:bg-orange-100 dark:hover:bg-orange-800 transition-colors"
                      >
                        View Bookings
                      </button>
                      <button
                        onClick={() => setCurrentStep(2)}
                        className="px-4 py-2 bg-orange-600 dark:bg-orange-700 text-white rounded-xl text-sm font-semibold hover:bg-orange-700 dark:hover:bg-orange-600 transition-colors"
                      >
                        Change Category
                      </button>
                    </>
                  )}

                  {postingError.type === "job-limit" && (
                    <>
                      <button
                        onClick={() => navigate("/requests/manage")}
                        className="px-4 py-2 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 rounded-xl text-sm font-semibold hover:bg-red-100 dark:hover:bg-red-800 transition-colors"
                      >
                        Manage Jobs
                      </button>
                      <button
                        onClick={() => setCurrentStep(2)}
                        className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded-xl text-sm font-semibold hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
                      >
                        Different Category
                      </button>
                    </>
                  )}

                  {(postingError.type === "duplicate-email" ||
                    postingError.type === "duplicate-phone") && (
                    <>
                      <button
                        onClick={() => {
                          setClientType("existing");
                          setCurrentStep(1);
                          setPostingError(null);
                        }}
                        className="px-4 py-2 bg-white dark:bg-gray-800 border border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400 rounded-xl text-sm font-semibold hover:bg-yellow-100 dark:hover:bg-yellow-800 transition-colors"
                      >
                        Search Existing Client
                      </button>
                      <button
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            newClientEmail: "",
                            newClientPhone: "",
                          }));
                          setPostingError(null);
                          setCurrentStep(1);
                        }}
                        className="px-4 py-2 bg-yellow-500 dark:bg-yellow-600 text-white rounded-xl text-sm font-semibold hover:bg-yellow-600 dark:hover:bg-yellow-500 transition-colors"
                      >
                        Change Details
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rules Notice */}
        <div className="bg-blue-100 dark:bg-blue-800 border border-blue-100 dark:border-blue-800 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-1 bg-blue-100 dark:bg-blue-800 rounded-full text-blue-600 dark:text-blue-300 mt-0.5">
              <Check size={14} />
            </div>
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-semibold mb-1">Validation Rules:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300 text-xs">
                <li>
                  Cannot post if client has active appointment in this category
                </li>
                <li>
                  Cannot post if client has pending, assigned, or work-started
                  job in this category
                </li>
                <li>Completed or cancelled jobs do not block new postings</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100 dark:border-dark-border">
        <button
          onClick={handleBack}
          className="px-6 py-2.5 rounded-2xl border border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-text font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
        >
          <ChevronLeft size={18} /> Back
        </button>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`px-8 py-3 rounded-2xl font-bold shadow-lg transition-all flex items-center gap-2 min-w-[160px] justify-center ${
            isSubmitting
              ? "bg-gray-400 cursor-not-allowed text-white"
              : "bg-primary hover:bg-primary/90 text-white shadow-primary/20 dark:shadow-none hover:shadow-xl hover:-translate-y-0.5"
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              <span>Posting...</span>
            </>
          ) : (
            <>
              <span>Post Job</span>
              <ChevronRight size={18} />
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            Post Service Request
          </h1>
        </div>
        <p className="text-gray-500 dark:text-dark-muted">
          Create a new job posting for a client. System validates against active
          appointments and jobs.
        </p>
      </div>

      {/* Success Message */}
      {successMsg && (
        <div className="mb-6 bg-green-100 dark:bg-green-800 border border-green-200 dark:border-green-800 rounded-2xl p-5 flex items-center gap-4 animate-in fade-in zoom-in duration-300">
          <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center flex-shrink-0">
            <Check size={24} className="text-green-600 dark:text-green-300" />
          </div>
          <div>
            <h3 className="font-bold text-green-900 dark:text-green-200 text-lg">
              {successMsg}
            </h3>
            <p className="text-green-700 dark:text-green-400 text-sm">
              Redirecting to job management...
            </p>
          </div>
        </div>
      )}

      {/* Progress Stepper */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-border mb-8">
        <div className="flex items-center justify-between relative px-2 md:px-12">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-2 bg-gray-100 dark:bg-gray-700 z-0 rounded-full"></div>
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 h-2 bg-primary z-0 transition-all duration-500 ease-out rounded-full"
            style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
          ></div>

          {[
            { num: 1, label: "Client" },
            { num: 2, label: "Category" },
            { num: 3, label: "Details" },
            { num: 4, label: "Review" },
          ].map((step) => (
            <div
              key={step.num}
              className="relative z-10 flex flex-col items-center gap-2 bg-card dark:bg-dark-surface px-2"
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 border-2 ${
                  step.num < currentStep
                    ? "bg-green-700 border-green-700 text-white shadow-lg"
                    : step.num === currentStep
                      ? "bg-primary border-primary text-white shadow-lg shadow-primary/30 scale-110"
                      : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-300 dark:text-gray-600"
                }`}
              >
                {step.num < currentStep ? (
                  <Check size={22} strokeWidth={3} />
                ) : (
                  <span className="font-bold text-lg">{step.num}</span>
                )}
              </div>
              <span
                className={`text-xs font-bold uppercase tracking-wider hidden sm:block ${
                  step.num <= currentStep
                    ? "text-primary"
                    : "text-gray-400 dark:text-dark-muted"
                }`}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-lg border border-gray-100 dark:border-dark-border overflow-hidden">
        <div className="p-8 md:p-10 min-h-[600px]">
          {currentStep === 1 && renderClientSelection()}
          {currentStep === 2 && renderCategorySelection()}
          {currentStep === 3 && renderJobDetails()}
          {currentStep === 4 && renderReview()}
        </div>

        {/* Footer Navigation - Only for steps 1-3 */}
        {currentStep < 4 && (
          <div className="px-8 py-6 bg-gray-100 dark:bg-gray-800 border-t border-gray-100 dark:border-dark-border flex justify-between items-center">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`px-6 py-2.5 rounded-2xl border font-semibold flex items-center gap-2 transition-all ${
                currentStep === 1
                  ? "opacity-0 pointer-events-none"
                  : "border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-text hover:bg-white dark:hover:bg-gray-800 hover:shadow-md"
              }`}
            >
              <ChevronLeft size={18} /> Back
            </button>

            <button
              onClick={handleNext}
              className="px-8 py-3 rounded-2xl font-bold shadow-lg transition-all flex items-center gap-2 bg-primary hover:bg-primary/90 text-white shadow-primary/20 dark:shadow-none hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
            >
              Next Step
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Help Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-400 dark:text-dark-muted">
          System validates job posting rules automatically
        </p>
      </div>
    </div>
  );
};

export default PostServiceRequest;
